import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/utils";

export function AffReportingClicksPage() {
  const now = new Date();
  const [from, setFrom] = useState(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7).toISOString().split("T")[0]);
  const [to, setTo] = useState(now.toISOString().split("T")[0]);

  const { data, isLoading } = useQuery({
    queryKey: ["aff-portal", "clicks", from, to],
    queryFn: async () => {
      const res = await api.post("/v1/portal/affiliate/reporting/clicks", {
        from: from + " 00:00:00", to: to + " 23:59:59", limit: 200,
      });
      if (!res.ok) throw new Error("Failed to load clicks");
      return (await res.json()).data || [];
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Click Report</h1>
          <p className="text-sm text-gray-500 mt-1">Raw click data for your traffic</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5" />
          <span className="text-gray-400">to</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs">Time</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs">Offer</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs">Country</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs">Device</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs">OS</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs">Sub1</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500 text-xs">Payout</th>
                <th className="text-center py-3 px-4 font-medium text-gray-500 text-xs">Unique</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={8} className="py-12 text-center text-gray-400">Loading...</td></tr>
              ) : !data?.length ? (
                <tr><td colSpan={8} className="py-12 text-center text-gray-400">No clicks found for this period.</td></tr>
              ) : data.map((row: Record<string, unknown>, i: number) => (
                <tr key={i} className="border-t border-gray-50 hover:bg-gray-50/50">
                  <td className="py-2 px-4 text-gray-600 text-xs font-mono">{String(row.created_at).substring(0, 19)}</td>
                  <td className="py-2 px-4 text-gray-700">{String(row.offer_id)}</td>
                  <td className="py-2 px-4 text-gray-700">{String(row.country_code || "-")}</td>
                  <td className="py-2 px-4 text-gray-700 text-xs">{String(row.device_type || "-")}</td>
                  <td className="py-2 px-4 text-gray-700 text-xs">{String(row.os || "-")}</td>
                  <td className="py-2 px-4 text-gray-500 text-xs truncate max-w-[120px]">{String(row.sub1 || "-")}</td>
                  <td className="py-2 px-4 text-right font-medium text-emerald-600">{formatCurrency(Number(row.payout || 0))}</td>
                  <td className="py-2 px-4 text-center">{row.is_unique ? "Y" : "N"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
